/**
 * Gift with purchase: keeps exactly one gift line in the cart while the rest of the cart
 * (excluding the gift) is at or above the threshold, and removes it when it drops below.
 * Config comes from snippets/cart-gwp-config.liquid. The price is zeroed by an automatic
 * discount in Shopify admin; this script only manages the cart line.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const configEl = document.getElementById('cart-gwp-config');

if (configEl && !window.__cartGwpInit) {
  window.__cartGwpInit = true;

  const config = JSON.parse(configEl.textContent);
  const GWP = 'wild-fig-mini';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cartUrl = root + 'cart';

  const getCart = () =>
    fetch(cartUrl + '.json', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then((r) =>
      r.json()
    );

  const sectionIds = () => {
    const ids = new Set();
    document.querySelectorAll('cart-items-component').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  };

  const postJson = async (url, payload) => {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();
    if (!res.ok || (data.status && data.status >= 400)) throw new Error(data.description || 'Cart error');
    return data;
  };

  const isGift = (item) => item.properties && item.properties._gwp === GWP;
  // Must match the collections in the Shopify discount (Ceremony In Bloom + Ceremony Flowers).
  const countsToward = (item) => !isGift(item) && (config.vendors || []).includes(item.vendor);

  /* ---------- Progress bar ---------- */

  let lastEligible = null;

  const formatTry = (kurus) => {
    const lira = kurus / 100;
    const opts = Number.isInteger(lira) ? { maximumFractionDigits: 0 } : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return new Intl.NumberFormat('tr-TR', opts).format(lira) + ' ₺';
  };

  const escapeHtml = (s) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]);

  function barHtml(eligible) {
    const remaining = config.threshold - eligible;
    const pct = Math.max(0, Math.min(100, Math.floor((eligible / config.threshold) * 100)));
    const name = escapeHtml(config.giftName);
    const text =
      remaining > 0
        ? escapeHtml(config.progressText).replace('[amount]', `<span class="cart-gwp__amount">${formatTry(remaining)}</span>`).replace('[gift]', `<span class="cart-gwp__gift">${name}</span>`)
        : escapeHtml(config.unlockedText).replace('[gift]', `<span class="cart-gwp__gift">${name}</span>`);
    return (
      `<p class="cart-gwp__text${remaining > 0 ? '' : ' is-unlocked'}">${text}</p>` +
      `<div class="cart-gwp__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${pct}" aria-label="${name}">` +
      `<span class="cart-gwp__fill" style="width:${pct}%"></span></div>`
    );
  }

  /** Put (or refresh) one bar above every cart totals block on the page. */
  function paintBars() {
    if (lastEligible === null) return;
    const show = config.available && lastEligible > 0;
    document.querySelectorAll('.cart-totals').forEach((totals) => {
      let bar = totals.previousElementSibling;
      if (!(bar instanceof HTMLElement) || !bar.hasAttribute('data-cart-gwp')) {
        if (!show) return;
        bar = document.createElement('div');
        bar.className = 'cart-gwp';
        bar.setAttribute('data-cart-gwp', '');
        totals.before(bar);
      }
      if (!show) {
        bar.remove();
        return;
      }
      const html = barHtml(lastEligible);
      // Compare against what we wrote last, not innerHTML (browser serialization can differ and loop the observer).
      if (bar.dataset.html !== html) {
        bar.dataset.html = html;
        bar.innerHTML = html;
      }
    });
  }

  // The drawer and cart page are re-rendered after every change; re-add the bar when that happens.
  let paintQueued = false;
  new MutationObserver(() => {
    if (paintQueued) return;
    paintQueued = true;
    requestAnimationFrame(() => {
      paintQueued = false;
      paintBars();
    });
  }).observe(document.body, { childList: true, subtree: true });

  let busy = false;
  let rerun = false;

  async function reconcile() {
    if (busy) {
      rerun = true; // a change landed mid-check; run again once this one finishes
      return;
    }
    busy = true;
    let deferred = null;
    try {
      const cart = await getCart();
      const giftLines = (cart.items || []).filter(isGift);
      const eligible = (cart.items || [])
        .filter(countsToward)
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const qualifies = config.available && eligible >= config.threshold;
      lastEligible = eligible;
      paintBars();

      // Work out the one change needed, if any.
      let request = null;
      if (qualifies && giftLines.length === 0) {
        request = { url: root + 'cart/add', payload: { items: [{ id: config.variantId, quantity: 1, properties: { _gwp: GWP } }] }, action: 'add' };
      } else if (!qualifies && giftLines.length > 0) {
        request = { url: root + 'cart/change', payload: { id: giftLines[0].key, quantity: 0 }, action: 'remove' };
      } else if (qualifies && (giftLines.length > 1 || giftLines[0].quantity > 1)) {
        // Only one gift per order: drop extra lines, clamp the first to 1.
        const extra = giftLines.slice(1);
        for (const line of extra) await postJson(root + 'cart/change', { id: line.key, quantity: 0 });
        request = { url: root + 'cart/change', payload: { id: giftLines[0].key, quantity: 1 }, action: 'change' };
      }
      if (!request) return;

      const sections = sectionIds();
      if (sections.length) {
        request.payload.sections = sections.join(',');
        request.payload.sections_url = window.location.pathname;
      }

      deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(
        new CartLinesUpdateEvent({
          action: request.action,
          context: 'cart',
          lines: [{ id: String(config.variantId), quantity: request.action === 'remove' ? 0 : 1 }],
          promise: deferred.promise,
        })
      );

      const data = await postJson(request.url, request.payload);
      const updated = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(updated),
        detail: { sections: data && data.sections, itemCount: updated.item_count, source: 'cart-gwp', didError: false },
      });
    } catch (error) {
      if (deferred) deferred.reject(error);
    } finally {
      busy = false;
      if (rerun) {
        rerun = false;
        reconcile();
      }
    }
  }

  // Check on page load (e.g. cart carried over from an earlier visit).
  reconcile();

  // Re-check after any other cart change; ignore the changes this script makes itself.
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    if (event.detail && event.detail.source === 'cart-gwp') return;
    const p = event.promise;
    if (!p) return;
    p.then((result) => {
      if (result && result.detail && result.detail.source === 'cart-gwp') return;
      reconcile();
    }).catch(() => {});
  });
}
